import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "../components/site/Reveal";
import Parallax from "../components/site/Parallax";
import WaitlistForm from "../components/site/WaitlistForm";
import KineticHero from "../components/site/KineticHero";
import PromoBand from "../components/site/PromoBand";
import FaqList from "../components/site/FaqList";
import { HOME } from "../constants/testIds";
import { IMAGES, SERVICE_OVERVIEW, CONTACT, BOOKING_URL, PORTRAIT } from "../lib/site";
import { useBlogPosts } from "../lib/useBlog";
import { useSeo, dentalClinicSchema, faqSchema } from "../lib/seo";

const EASE = [0.22, 1, 0.36, 1];


const FAQS = [
  {
    q: "When is Alae Dental opening?",
    a: "We open the week of October 13, 2026, at 9097 Jane St in Vaughan. Booking is already open, so you can reserve your preferred day and time online now, or leave your details and we'll help you find a spot.",
  },
  {
    q: "Are you accepting new patients and families?",
    a: "Yes. We're accepting new patients now for appointments from our opening week onward. Alae Dental is built to care for every member of the family, from a child's first visit to comprehensive adult treatment.",
  },
  {
    q: "How do I book an appointment?",
    a: "Use the Book Online button anywhere on the site. It opens our secure booking system where you can pick your day and time in minutes. Prefer to be called back? Fill out the form below and we'll be in touch within 24 hours.",
  },
  {
    q: "What are your hours?",
    a: "Monday and Wednesday from 9:00 to 19:00, Tuesday, Thursday and Friday from 9:00 to 18:00, and Saturday from 9:00 to 15:00. We are closed on Sunday. For urgent concerns, call us and we'll do everything we can to help.",
  },
  {
    q: "What if I have a dental emergency?",
    a: `Call us at ${CONTACT.phoneLabel}. We'll do everything we can to help right away or direct you to immediate care.`,
  },
  {
    q: "Is Dr. Mirza experienced with Invisalign and cosmetic work?",
    a: "Yes. Dr. Mirza is a General Dentist with over 13 years of clinical experience and was previously recognized as an Invisalign Gold Provider while practising in Florida. Treatment recommendations are always based on an individual assessment.",
  },
];

export default function Home() {
  const { posts } = useBlogPosts(3);

  useSeo({
    title:
      "Alae Dental. Family and Cosmetic Dentist in Vaughan, ON | Dr. Nausheen Mirza",
    description:
      "A new dental home for Vaughan. Family, cosmetic and implant dentistry from Dr. Nausheen Mirza, General Dentist. Book online today.",
    path: "/",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [dentalClinicSchema(), faqSchema(FAQS)],
    },
    jsonLdId: "home-jsonld",
  });

  return (
    <main className="bg-linen text-umber">
      {/* Signature Hero. Kinetic, masked line reveal */}
      <KineticHero />

      {/* Grand Opening promo band (temporary) */}
      <PromoBand />

      {/* Philosophy */}
      <section
        id="philosophy"
        data-testid={HOME.philosophy}
        className="bg-paper paper-grain py-28 md:py-44"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="font-meta text-forest mb-6">Our Philosophy</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-umber tracking-tight">
                Where calm meets{" "}
                <span className="font-display-italic">competence.</span>
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-16">
            <Reveal delay={150}>
              <blockquote className="border-l-2 border-forest pl-6 md:pl-8">
                <p className="font-display-italic text-2xl md:text-3xl leading-snug text-umber">
                  "After years of practicing and teaching, I knew exactly what I
                  wanted to create. A practice where I could deliver dentistry
                  the way I believe it should be practiced, with the care and
                  attention every patient deserves."
                </p>
                <footer className="font-meta text-umber/70 mt-6">
                  Dr. Nausheen Mirza
                </footer>
              </blockquote>
            </Reveal>

            <Reveal delay={250}>
              <p className="mt-12 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
                A new dental home for Vaughan, where the quality of your care
                and the way you experience it matter equally. Alae Dental was
                founded by Dr. Nausheen Mirza, shaped by more than 13 years in
                private and corporate dental practice and her time in clinical
                education. The practice reflects what she values most in dental
                care: high standards, genuine attention to each patient, and the
                time to do things properly.
              </p>
            </Reveal>

            <Reveal delay={300}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
                Every aspect of Alae Dental has been considered with both your
                care and your experience in mind. Modern technology and
                comprehensive care are paired with clear communication,
                unhurried appointments, and a more personal approach to
                dentistry.
              </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  title: "Continuity of care.",
                  body:
                    "Care built on familiarity, trust, and an understanding of your history and goals, over time.",
                },
                {
                  title: "Unhurried by design.",
                  body:
                    "Appointments are thoughtfully paced, with your time and care given our full attention and protected from unnecessary interruptions. There's time to ask questions, understand your options, and move through your appointment without feeling rushed.",
                },
                {
                  title: "Comfort, considered.",
                  body:
                    "From the environment we've created to the way we communicate and deliver care, comfort is considered at every step.",
                },
              ].map((p, i) => (
                <Reveal key={p.title} delay={i * 100}>
                  <div>
                    <div className="font-meta text-forest mb-3">
                      0{i + 1}
                    </div>
                    <h3 className="font-display text-xl text-umber mb-3">
                      {p.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-umber/80">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Dr. Mirza */}
      <section
        id="dr-mirza"
        data-testid={HOME.drMirza}
        className="bg-linen paper-grain py-28 md:py-40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative max-w-[300px] sm:max-w-[340px] mx-auto lg:max-w-none">
                {/* Accent block behind portrait (spotlight frame) */}
                <div
                  aria-hidden="true"
                  className="absolute -left-4 -top-4 md:-left-6 md:-top-6 w-24 h-24 md:w-40 md:h-40 border border-forest/50"
                />
                <div
                  aria-hidden="true"
                  className="absolute -right-3 -bottom-3 md:-right-5 md:-bottom-5 w-16 h-16 md:w-28 md:h-28 bg-forest"
                />
                {/* Portrait with masked reveal */}
                <div className="relative aspect-[4/5] overflow-hidden shadow-paper">
                  <motion.img
                    initial={{ scale: 1.15, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 1.6, ease: EASE }}
                    src={PORTRAIT.drMirza}
                    alt={PORTRAIT.drMirzaAlt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 20%" }}
                  />
                  {/* Diagonal reveal wipe */}
                  <motion.div
                    aria-hidden="true"
                    initial={{ scaleY: 1 }}
                    whileInView={{ scaleY: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 1.3, ease: EASE, delay: 0.1 }}
                    className="absolute inset-0 bg-linen origin-bottom"
                  />
                  {/* Subtle spotlight overlay */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 30%, transparent 45%, rgba(28,56,41,0.15) 100%)",
                    }}
                  />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-12">
            <Reveal>
              <div className="font-meta text-forest mb-6">Founder</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber">
                Founded by <span className="font-display-italic">Dr. Nausheen Mirza</span>
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-umber/85">
                After more than 13 years in private and corporate practice and
                clinical education across Canada and the United States, Dr.
                Mirza returned home to Woodbridge, the community she has called
                home since the age of two, to create Alae Dental. Her vision was
                to create a practice where patients receive the time, attention,
                and quality of care she believes they deserve.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-umber/85">
                Dr. Mirza is a General Dentist who earned her Doctor of Dental
                Surgery from NYU College of Dentistry in 2013, completed an
                Implant Fellowship through the University of Florida Implant
                Educators program, and pursued additional training through Spear
                Education and the Las Vegas Institute. She also served for
                several years as a Clinical Instructor at the University of
                Toronto Faculty of Dentistry. During her years practicing in
                Florida, Dr. Mirza was recognized as an Invisalign Gold Provider
                and was voted one of Tampa's Top Dentists by Tampa Style
                Magazine.
              </p>
            </Reveal>
            <Reveal delay={400}>
              <Link
                to="/about"
                data-testid={HOME.drMirzaCta}
                className="inline-flex items-center gap-3 mt-10 group text-[12px] tracking-[0.22em] uppercase text-forest py-3 min-h-[44px]"
              >
                Read Dr. Mirza's Full Story
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* The Experience */}
      <section
        id="experience"
        data-testid={HOME.experience}
        className="bg-paper paper-grain py-28 md:py-40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="font-meta text-forest mb-6">The Experience</div>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber">
                  Care that doesn't feel like a{" "}
                  <span className="font-display-italic">routine.</span>
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-8 text-base md:text-lg leading-relaxed text-umber/85 max-w-md">
                  From your first conversation with us through every stage of
                  your care, the Alae experience is designed to feel personal,
                  attentive, and considered.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7 lg:pl-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
                {[
                  {
                    title: "Focused appointments",
                    body: "Your appointment time is reserved for you, with our attention focused on your care.",
                  },
                  {
                    title: "Modern technology",
                    body: "Digital imaging and modern technology support diagnosis, treatment planning, and a more seamless patient experience.",
                  },
                  {
                    title: "Clear communication",
                    body: "We take the time to explain what we are doing, discuss your options, and answer your questions along the way.",
                  },
                  {
                    title: "A calming environment",
                    body: "A warm, thoughtfully designed space created with your comfort in mind.",
                  },
                ].map((item, i) => (
                  <Reveal key={item.title} delay={i * 80}>
                    <div className="border-t border-umber/15 pt-6">
                      <div className="font-meta text-forest mb-3">
                        0{i + 1}
                      </div>
                      <h3 className="font-display text-2xl text-umber mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-umber/80">
                        {item.body}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section
        id="services"
        data-testid={HOME.servicesOverview}
        className="bg-linen paper-grain py-28 md:py-40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="font-meta text-forest mb-6">Services</div>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber">
                  Comprehensive care for{" "}
                  <span className="font-display-italic">every stage</span> of
                  life.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:pt-10">
              <Reveal delay={200}>
                <p className="text-base md:text-lg leading-relaxed text-umber/85">
                  From routine cleanings to complete smile restorations, Alae
                  Dental offers the full range of modern dental care, all under
                  one roof, all delivered with the same unhurried attention.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Editorial list of services */}
          <div className="border-t border-umber/20">
            {SERVICE_OVERVIEW.map((s, i) => (
              <Reveal key={s.key} delay={i * 60}>
                <Link
                  to={`/services#${s.key}`}
                  data-testid={HOME.serviceCard(i)}
                  className="group block border-b border-umber/20 py-8 md:py-10 transition-colors duration-300 hover:bg-paper/60"
                >
                  <div className="grid grid-cols-12 gap-6 items-start md:items-center">
                    <div className="col-span-2 md:col-span-1 font-meta text-forest pt-1">
                      {s.number}
                    </div>
                    <div className="col-span-10 md:col-span-4">
                      <h3 className="font-display text-2xl md:text-3xl text-umber group-hover:text-forest transition-colors">
                        {s.title}
                      </h3>
                    </div>
                    <div className="col-span-12 md:col-span-6 text-umber/80 text-sm md:text-base leading-relaxed md:pl-6">
                      {s.blurb}
                    </div>
                    <div className="col-span-12 md:col-span-1 flex md:justify-end">
                      <ArrowUpRight
                        size={22}
                        className="text-umber/50 group-hover:text-forest transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 text-center md:text-left">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-forest py-3 min-h-[44px]"
            >
              Explore all services
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section
        id="journal"
        data-testid={HOME.blogPreview}
        className="bg-linen paper-grain py-28 md:py-40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="font-meta text-forest mb-6">The Journal</div>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber">
                  From the <span className="font-display-italic">practice.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:pt-8">
              <Reveal delay={200}>
                <p className="text-base md:text-lg leading-relaxed text-umber/85">
                  Practical guidance on caring for your smile, what to expect
                  from treatment, and the thinking behind how we practice.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {(posts || []).slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={HOME.blogCard(i)}
                  className="group block"
                >
                  <div className="font-meta text-umber/60 mb-4">
                    Article · {(i + 1).toString().padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-snug text-umber group-hover:text-forest transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-sm leading-relaxed text-umber/80 line-clamp-3">
                    {p.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase text-forest">
                    Read article <ArrowUpRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-14">
            <Link
              to="/blog"
              className="inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-forest py-3 min-h-[44px]"
            >
              Visit the Journal <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid={HOME.faq}
        className="bg-paper paper-grain py-28 md:py-40"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="mb-14">
            <Reveal>
              <div className="font-meta text-forest mb-6">FAQ</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-umber tracking-tight">
                Questions, <span className="font-display-italic">answered.</span>
              </h2>
            </Reveal>
          </div>

          <Reveal delay={200}>
            <FaqList items={FAQS} testId={HOME.faqItem} />
          </Reveal>
        </div>
      </section>

      {/* Booking and callback */}
      <section
        id="waitlist"
        data-testid={HOME.waitlistSection}
        className="relative bg-linen paper-grain py-28 md:py-40 overflow-hidden"
      >
        <Parallax speed={0.08} className="absolute inset-0 opacity-30">
          {/* PLACEHOLDER: replace with real photography */}
          <img
            src={IMAGES.waitlist}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-[115%] object-cover"
          />
          <div className="absolute inset-0 bg-linen/60" />
        </Parallax>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Book Your Visit</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber max-w-3xl">
              Ready when <span className="font-display-italic">you are.</span>
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
              Book online in minutes, or leave your details below and we'll
              reach out within 24 hours to help you find the right time.
            </p>
          </Reveal>

          <Reveal delay={250}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300 text-center"
              >
                Book Online
              </a>
              <a
                href="#callback-form"
                className="border border-umber/40 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-all duration-300 text-center"
              >
                Request a Callback
              </a>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div
              id="callback-form"
              className="mt-14 bg-paper/80 backdrop-blur-sm border border-umber/15 p-8 md:p-12 shadow-paper scroll-mt-24"
            >
              <WaitlistForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
